import {EventEmitter, Injectable} from '@angular/core';
import {map, tap} from "rxjs/operators";
import {OnboardingApiControllerService, BranchControllerService, OfficerControllerService} from "falcon-api";
import {EvaluateRequest, SalesForceAlloyObject, KycControllerService} from "skyhopper-api";
import {NGXLogger} from "ngx-logger";
import {State} from '../models/state';
import {IncomeSource} from '../models/income-source';
import {Country} from '../models/country';
import {AnnualIncome} from '../models/annual-income';
import {Occupation} from '../models/occupation';
import {Observable} from "rxjs";
import {Title} from "@angular/platform-browser";
import {FormGroup} from "@angular/forms";
import {Router} from "@angular/router";
import {ApplicationLogsRequest} from "leia-api";
import { OfficerCodeData } from '../models/officer-code-info';

@Injectable({
  providedIn: 'root'
})
export class FacadeService {
  defaultTitle: string = "Coruscant | Consumer Onboarding Admin";
  filterDataOnboarding: any;
  filterDataJointMaintenance: any;
  appliedSearchText: string = "";
  loadSearchText: string = "";
  appliedDate: string = "";
  stateList: State[] = [];
  pageNumber: number = 0;

  constructor(private onboardingApiService: OnboardingApiControllerService,
              private logger: NGXLogger,
              private branchControllerService: BranchControllerService,
              private officerControllerService: OfficerControllerService,
              private skyhopperKycService: KycControllerService,
              private titleService: Title,
              private router: Router,) {
  }

  setPageTitleContext(description: string) {
    this.titleService.setTitle(`${description} - ${this.defaultTitle}`);
  }

  /**
   * get all income sources
   */
  async getAllIncomeSources(): Promise<Array<IncomeSource>> {
    return new Promise((resolve, reject) => {
      let results = this.onboardingApiService.getAllIncomeSources();
      results.pipe(
        map((items) => items
            .map(item => (new IncomeSource(item.id as number, item.name as string))),
          tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
        ))
        .subscribe((mappedItems) => {
          resolve(mappedItems);
        });
    });
  }

  /**
   * get all Occupations
   */
  async getAllOccupations(): Promise<Array<Occupation>> {
    return new Promise((resolve, reject) => {
      let results = this.onboardingApiService.getAllOccupations();
      results.pipe(
        map((items) => items
            .map(item => (new Occupation(item.id as number, item.name as string))),
          tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
        ))
        .subscribe((mappedItems) => {
          resolve(mappedItems);
        });
    });
  }

  /***
   * get all of the countries from Falcon
   */
  async getCountries(): Promise<Array<Country>> {
    return new Promise((resolve, reject) => {
      let results = this.onboardingApiService.getAllCountries();
      results.pipe(
        map((items) => items
            .map(item => (new Country(item.countryCode!, item.countryName!, item.isofacSanctioned!, item.international!, item.abbreviation1!, item.abbreviation2!))),
          tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
        ))
        .subscribe((mappedItems) => {
          resolve(mappedItems);
        });
    });
  }

  /***
   * get all of the income ranges, sorted by ID (which is smallest to largest)
   */
  async getAnnualIncomes(): Promise<Array<AnnualIncome>> {
    return new Promise((resolve, reject) => {
      let results = this.onboardingApiService.getAllIncomes();
      results.pipe(
        map((items) => items
            .map(item => (new AnnualIncome(item.id, item.minValue, item.maxValue, item.displayText))),
          tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
        ))
        .subscribe((mappedItems) => {
          resolve(mappedItems.sort((n1, n2) => {
            if (n1.annualIncomeId > n2.annualIncomeId) return 1;
            if (n1.annualIncomeId < n2.annualIncomeId) return -1;
            return 0;
          }));
        });
    });
  }

  /**
   * get all States
   */
  async loadAllStates(): Promise<Array<State>> {
    return new Promise((resolve, reject) => {
      let results = this.onboardingApiService.getAllStates();
      results.pipe(
        map((items) => items
            .map(item => (new State(item.stateProvinceId as number, item.countryCode!, item.stateName as string, item.stateAbbreviation as string))),
          tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
        ))
        .subscribe((mappedItems) => {
          resolve(mappedItems);
        });
    });
  }

  async setStateList() {
    await this.loadAllStates().then((states => {
        this.addStorageItem("states", JSON.stringify(states));
      }
    ));
  }

  executeAlloyEvaluate(evaluateRequest: EvaluateRequest, documentVerify: boolean, alloyWorkFlowId: string, entityTokenId: string): Observable<SalesForceAlloyObject> {
    if (!documentVerify) return this.skyhopperKycService.evaluate(evaluateRequest, alloyWorkFlowId);
    else return this.skyhopperKycService.documentVerification(evaluateRequest, entityTokenId, alloyWorkFlowId);
  }

  /***
   * add key/value pair to session storage
   * @param key
   * @param value
   */
  addStorageItem(key: string, value: string): void {
    sessionStorage.setItem(key, value);
  }

  /***
   * remove key/value pair to session storage
   * @param key
   */
  removeStorageItem(key: string): void {
    sessionStorage.removeItem(key);
  }

  /***
   * clear the session storage
   */
  clearStorage(): void {
    sessionStorage.clear();
  }

  /***
   * get the storage item by key
   * @param key
   */
  getStorageItem(key: string): string {
    return sessionStorage.getItem(key)!;
  }

  getBranchList(): Observable<any> {
    return this.branchControllerService.getBranches();
  }

  getOfficerList(): Observable<any> {
    return this.officerControllerService.getBranchOfficers();
  }

  addFilterData(data: any, jointApplicationsRequest: boolean) {
    if (!jointApplicationsRequest) this.filterDataOnboarding = data;
    else this.filterDataJointMaintenance = data;
  }

  addFilterSearchText(appliedSearchText: string) {
    this.appliedSearchText = appliedSearchText;
  }

  getFilterSearchText() {
    return this.appliedSearchText;
  }

  addFilterLoadingSearchText(loadSearchText: string) {
    this.loadSearchText = loadSearchText;
  }

  addAppliedDate(appliedDate: string) {
    this.appliedDate = appliedDate;
  }

  /* ====================================================================================================  */

  // Google Places/Maps
  /**
   * Fill addresses.
   * @param place
   */
  fillInAddress(place: any, formGroup: FormGroup, route: string) {
    let address1: string = "";
    let suggestedCity: string = ""
    let postcode: string = "";
    let stateAbbr: string = "";

    this.logger.log("Place = " + JSON.stringify(place));

    // Get each component of the address from the place details,
    // and then fill-in the corresponding field on the form.
    // place.address_components are google.maps.GeocoderAddressComponent objects
    // which are documented at http://goo.gle/3l5i5Mr
    for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
      // @ts-ignore remove once typings fixed
      const componentType = component.types[0];

      switch (componentType) {
        case "street_number": {
          address1 = `${component.long_name} ${address1}`;
          break;
        }

        case "route": {
          address1 += component.short_name;
          break;
        }

        case "postal_code": {
          postcode = `${component.long_name}${postcode}`;
          break;
        }

        case "postal_code_suffix": {
          postcode = `${postcode}-${component.long_name}`;
          break;
        }

        case "locality":
          suggestedCity = component.long_name;
          break;

        case "administrative_area_level_1": {
          stateAbbr = component.short_name;
          break;
        }

        case "country":
          break;
      }
    }

    // Bug 398139: some addresses not populating city information
    // If we couldn't pull the city info from the locality array, pull from the vicinity
    if (!suggestedCity) suggestedCity = place.vicinity ?? "";

    if (route === "nao") {
      const {houseNumber, street, city, state, zipCode} = formGroup.controls;

      street.setValue("");
      houseNumber.setValue(address1);
      zipCode.setValue(postcode);
      city.setValue(suggestedCity);
      state.setValue((this.stateList.find(state => state.stateAbbr === stateAbbr))?.stateAbbr);
    } else {
      const {addressLine1, addressLine2, city, state, zipCode} = formGroup.controls;

      addressLine2.setValue("");
      addressLine1.setValue(address1);
      zipCode.setValue(postcode);
      city.setValue(suggestedCity);
      state.setValue((this.stateList.find(state => state.stateAbbr === stateAbbr))?.id);
    }

    formGroup.updateValueAndValidity();
  }


  public refreshPage() {
    const currentUrl = this.router.url;
    const previousReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute;
    const previousUrlNavigation = this.router.onSameUrlNavigation;

    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.router.navigate([currentUrl]);

    //Reverting it back once reload complete - This fix the 'Back To Application' not redirecting to Application search page.
    setTimeout(() => {
      this.router.routeReuseStrategy.shouldReuseRoute = previousReuseRoute;
      this.router.onSameUrlNavigation = previousUrlNavigation;
    }, 0);
  }

  // Officer Code info API Call and filter out logic
  getOfficerCode(brandId: number, version: number): Promise<OfficerCodeData[]> {
    return new Promise((resolve, reject) => {
      this.onboardingApiService.getSettingsForBrand(brandId, version).pipe(
        map(brands => {
          const dropdownList = brands.find(brand => brand.name === 'officer-codes-info')?.value ?? '[]';
          return JSON.parse(dropdownList);
        })
      ).subscribe({
        next: (result) => {
          const activeItems = result.filter((item: {active: boolean}) => item.active);
          resolve(activeItems);
        },
        error: (err) => reject(err)
      });
    });
  }

}
